// Histórico do global search guardado em localStorage: termos buscados e itens
// acessados recentemente (de qualquer tipo: cliente, cobrança, recorrência,
// plano, conta a pagar, NFSe, contrato). Mostrado quando a busca abre vazia,
// para o usuário voltar rápido ao que já viu.

import { readGroupSession } from 'lib/merchant-group'
import { parseCookies } from 'nookies'

const KEY_PREFIX = 'globalSearch:'
const MAX_ITEMS = 5

// SC-10999: o histórico é de quem busca e de onde busca. Guardado numa chave
// única, termos e itens (com nome de cliente) passavam de uma empresa para
// outra e de um usuário para outro no mesmo navegador, e reabrir um item de
// outra empresa ia com o token errado. A chave leva o usuário da empresa ou,
// na visão multi-empresas, o grupo.
const scopeKey = () => {
  const group = readGroupSession()
  if (group) return `group:${group.merchant_group_uuid}`
  return `mu:${parseCookies()['nextauth.merchant_user_uuid'] || 'anon'}`
}
const termsKey = () => `${KEY_PREFIX}recentTerms:${scopeKey()}`
const itemsKey = () => `${KEY_PREFIX}recentItems:${scopeKey()}`

// Tipos espelham os InfoKeys do SearchModal.
export type RecentItemType =
  | 'client'
  | 'billing'
  | 'recurrence'
  | 'product'
  | 'accounts_payable'
  | 'nfse'
  | 'contract'

// Snapshot leve do resultado acessado. Guarda o necessário para reexibir
// (title/subtitle) e para reabrir do jeito certo (type + uuid: navega para a
// página ou abre o modal de detalhe conforme o tipo).
export type RecentItem = {
  uuid: string
  type: RecentItemType
  title: string
  subtitle?: string
  // SC-10999: empresa do item, para reabrir na empresa certa na visão do grupo
  merchantUuid?: string
}

const isBrowser = () => typeof window !== 'undefined'

const read = <T>(key: string): T[] => {
  if (!isBrowser()) return []
  try {
    const raw = window.localStorage.getItem(key)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const write = <T>(key: string, value: T[]) => {
  if (!isBrowser()) return
  try {
    window.localStorage.setItem(key, JSON.stringify(value.slice(0, MAX_ITEMS)))
  } catch {
    // localStorage cheio/indisponível — histórico é best-effort.
  }
}

export const getRecentTerms = (): string[] => read<string>(termsKey())

export const addRecentTerm = (term: string) => {
  const clean = term.trim()
  if (!clean) return
  const existing = getRecentTerms().filter(
    (t) => t.toLowerCase() !== clean.toLowerCase()
  )
  write(termsKey(), [clean, ...existing])
}

export const getRecentItems = (): RecentItem[] => read<RecentItem>(itemsKey())

export const addRecentItem = (item: RecentItem) => {
  if (!item?.uuid) return
  // Dedup pelo par (type, uuid): o mesmo uuid pode existir em tipos diferentes.
  const existing = getRecentItems().filter(
    (i) => !(i.uuid === item.uuid && i.type === item.type)
  )
  write(itemsKey(), [item, ...existing])
}

// Apaga o histórico de todos os escopos (logout: o navegador pode ser
// compartilhado). Inclui as chaves antigas, sem escopo.
export const clearSearchHistory = () => {
  if (!isBrowser()) return
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith(KEY_PREFIX))
      .forEach((key) => window.localStorage.removeItem(key))
  } catch {
    // best-effort
  }
}
